/**
 * Typing somebody's name into a comment.
 *
 * THE BODY STAYS PLAIN TEXT. A mention is "@Amara Okonkwo" in the message and a resolved
 * identifier beside it -- never a marker the reader sees, and never something parsed back out of
 * the text later. Parsing at read time is how mentions break when somebody is renamed, and an "@"
 * in a comment on a procedure is usually not a mention at all: @121°C, a pasted address, a part
 * number. So what somebody picked is remembered as they pick it.
 *
 * WHICH MEANS IT CAN GO STALE WHILE THEY TYPE. Somebody picks a name, changes their mind and
 * deletes it from the sentence -- and without `stillNamed` the comment would go out carrying a
 * mention of a person whose name is nowhere in it, which is how somebody gets told about a
 * document for no reason they can see. Checked at submit, against the text actually being sent.
 */

export type Person = { AccountUserUSID: string; Name: string; Email?: string };

/** The word being typed after an "@", or null if the caret is not in one. */
export function mentionQuery(text: string, caret: number): { at: number; query: string } | null {
    const before = text.slice(0, caret);
    const at = before.lastIndexOf("@");
    if (at < 0) return null;

    // AN "@" ONLY STARTS A MENTION AT A WORD BOUNDARY, so an email address does not open the
    // picker halfway through itself.
    if (at > 0 && !/\s|[(\[]/.test(before[at - 1])) return null;

    const query = before.slice(at + 1);
    // ONE WORD, AND NOT A LONG ONE. A name has a space in it, so the query may hold a single space
    // -- "Amara Ok" must still find her -- but a whole sentence after an "@" is not somebody
    // hunting for a person.
    if (!/^[^\s]*(\s[^\s]*)?$/.test(query) || query.length > 40) return null;

    return { at, query };
}

/** `text` with the half-typed mention replaced by this person's name. */
export function withMention(text: string, caret: number, person: Person):
        { text: string; caret: number } {
    const found = mentionQuery(text, caret);
    if (!found) return { text, caret };

    const inserted = `@${person.Name} `;
    const next = text.slice(0, found.at) + inserted + text.slice(caret);
    return { text: next, caret: found.at + inserted.length };
}

/**
 * The chosen people whose names are still in the message.
 *
 * MATCHED ON "@Name", which is what was inserted. A name that appears without its "@" is somebody
 * being talked ABOUT rather than talked TO, and that is not a mention.
 */
export function stillNamed(body: string, chosen: Person[]): Person[] {
    const seen = new Set<string>();
    return chosen.filter((person) => {
        if (seen.has(person.AccountUserUSID)) return false;
        if (!body.includes(`@${person.Name}`)) return false;
        seen.add(person.AccountUserUSID);
        return true;
    });
}

/** The ranges of `body` that are mentions, for drawing them differently. */
export function mentionRanges(body: string, named: { Name: string | null }[]):
        Array<{ start: number; end: number }> {
    const ranges: Array<{ start: number; end: number }> = [];
    for (const person of named) {
        if (!person.Name) continue;
        const needle = `@${person.Name}`;
        let from = body.indexOf(needle);
        while (from >= 0) {
            ranges.push({ start: from, end: from + needle.length });
            from = body.indexOf(needle, from + needle.length);
        }
    }
    // LONGEST FIRST AT EACH START, then non-overlapping -- two people called "Amara" and
    // "Amara Okonkwo" must not draw one inside the other.
    ranges.sort((a, b) => a.start - b.start || b.end - a.end);
    const out: Array<{ start: number; end: number }> = [];
    for (const range of ranges) {
        if (!out.length || range.start >= out[out.length - 1].end) out.push(range);
    }
    return out;
}
